import { OrderPurpose } from 'lib/graphql/storeadmin'
import {
  Checkbox,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  RowProps,
  Select,
} from 'components/common'
import { CustomizedModalProps } from './types'

export interface BookingDetail {
  numberOfPeople: number
  purpose: OrderPurpose
  entryFee?: boolean
  saleOfGoods?: boolean
  contactOfRepresentative?: {
    name?: string
    email?: string
    phone?: string
  }
  exceed?: number
  discount?: number
}

export interface BookingDetailsEditModalProps
  extends Omit<CustomizedModalProps, 'onOk'> {
  bookingDetail: BookingDetail
  isArena: boolean
  onOk: (values: BookingDetail) => void
  onClose: (visible: boolean) => void
}

const rowProps: RowProps = {
  gutter: 16,
  align: 'middle',
}

export const BookingDetailsEditModal = ({
  visible,
  bookingDetail,
  isArena,
  onOk,
  onClose,
  ...rest
}: BookingDetailsEditModalProps): JSX.Element => {
  const [form] = Form.useForm<BookingDetail>()
  const onSubmit = (values: BookingDetail) => {
    onOk(values)
    onClose(false)
  }

  return (
    <Modal
      visible={visible}
      title="予約詳細を編集する"
      okText="保存する"
      cancelText="キャンセル"
      onOk={() => {
        form
          .validateFields()
          .then((values) => {
            onSubmit(values)
          })
          .catch(console.warn)
      }}
      onCancel={() => onClose(false)}
      {...rest}
    >
      <Form form={form} name="form_in_modal">
        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue }) => (
            <>
              <Row {...rowProps}>
                {!isArena && (
                  <Col span={12}>
                    <Form.Item
                      label="利用目的"
                      name="purpose"
                      initialValue={
                        bookingDetail?.purpose
                          ? bookingDetail?.purpose
                          : OrderPurpose.General
                      }
                      rules={[{ required: true }]}
                    >
                      <Select>
                        <Select.Option
                          key={OrderPurpose.General}
                          value={OrderPurpose.General}
                        >
                          一般利用
                        </Select.Option>
                        <Select.Option
                          key={OrderPurpose.Event}
                          value={OrderPurpose.Event}
                        >
                          イベント利用
                        </Select.Option>
                        {/* その他は一旦必要ないのでコメントアウト
                      <Select.Option
                        key={OrderPurpose.Other}
                        value={OrderPurpose.Other}
                      >
                        その他
                      </Select.Option>
                      */}
                      </Select>
                    </Form.Item>
                  </Col>
                )}
                <Col span={12}>
                  <Form.Item
                    label="利用予定人数"
                    name="numberOfPeople"
                    initialValue={
                      bookingDetail?.numberOfPeople > 0
                        ? bookingDetail?.numberOfPeople
                        : 1
                    }
                    rules={[{ required: true }]}
                  >
                    <InputNumber min={1} />
                  </Form.Item>
                </Col>
              </Row>
              {getFieldValue('purpose') === OrderPurpose.Event && (
                <>
                  <Form.Item name="contactOfRepresentative" noStyle>
                    <Form.Item
                      name={['contactOfRepresentative', 'name']}
                      label="責任者氏名"
                      tooltip="予約者とイベントの責任者が異なる場合に入力してください。"
                      initialValue={
                        bookingDetail?.contactOfRepresentative?.name
                      }
                      rules={[
                        { required: false },
                        {
                          type: 'string',
                          whitespace: true,
                          message: '空白のみを入力することはできません。',
                        },
                      ]}
                    >
                      <Input autoComplete="off" />
                    </Form.Item>
                    <Form.Item
                      name={['contactOfRepresentative', 'email']}
                      label="責任者連絡先メールアドレス"
                      tooltip="予約者とイベントの責任者の連絡先が異なる場合に入力してください。"
                      initialValue={
                        bookingDetail?.contactOfRepresentative?.email
                      }
                      rules={[
                        { required: false },
                        {
                          type: 'email',
                          message: 'メールアドレスの形式が正しく有りません。',
                        },
                      ]}
                    >
                      <Input autoComplete="off" />
                    </Form.Item>
                    <Form.Item
                      name={['contactOfRepresentative', 'phone']}
                      label="責任者連絡先電話番号"
                      tooltip="予約者とイベントの責任者の連絡先が異なる場合に入力してください。"
                      initialValue={
                        bookingDetail?.contactOfRepresentative?.phone
                      }
                      rules={[
                        { required: false },
                        {
                          pattern: /^0[0-9]{9,10}$/,
                          message: '電話番号の形式が正しく有りません。',
                        },
                      ]}
                    >
                      <Input autoComplete="off" />
                    </Form.Item>
                  </Form.Item>

                  <Row {...rowProps}>
                    <Col span={12}>
                      <Form.Item
                        label="物品販売"
                        name="saleOfGoods"
                        valuePropName="checked"
                        initialValue={
                          typeof bookingDetail?.saleOfGoods === 'boolean'
                            ? bookingDetail?.saleOfGoods
                            : false
                        }
                        rules={[{ required: true }]}
                        noStyle
                      >
                        <Checkbox>物品販売あり</Checkbox>
                      </Form.Item>
                    </Col>

                    <Col span={12}>
                      <Form.Item
                        label="入場料の徴収"
                        name="entryFee"
                        valuePropName="checked"
                        initialValue={
                          typeof bookingDetail?.entryFee === 'boolean'
                            ? bookingDetail?.entryFee
                            : false
                        }
                        rules={[{ required: true }]}
                        noStyle
                      >
                        <Checkbox>入場料の徴収あり</Checkbox>
                      </Form.Item>
                      <Form.Item
                        name="exceed"
                        initialValue={
                          bookingDetail?.exceed ? bookingDetail?.exceed : 0
                        }
                        hidden
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              )}
            </>
          )}
        </Form.Item>
      </Form>
    </Modal>
  )
}
